import { navigatorDebugObject } from "./navigatorDebug";
import { useNavigatorConnectionEndpoints } from "../../connectionAnalyser/useNavigatorConnectionEndpoints";
// no itx for this file
import "./style.scss";
import React, { useEffect, useRef, useMemo, useLayoutEffect, useCallback, useState } from "react";
import type { State } from "../../State";
import BaseModel from "../../models/BaseModel";
import ContextMenu from "../NavigationTree2/ContextMenu";
import { panelResized } from "../PanelResizer";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import NavigationTreeNodes from "./NavigationTreeNodes";
import EditableText from "../EditableText";
import BaseObject from "../../models/BaseObject";
import Browser from "../../browser";
import Logger from "../../Logger";
import { TreeContext, type SecondaryNavigatorTarget } from "./TreeContext";
import SearchBar from "../ProjectSelection/SearchBar";
import { adjustMenu } from "./NavigationTreeNode";
import { NavigatorModel, navigatorRows, type NavigatorChildren } from "./NavigatorModel";
import { navigatorChildren } from "./navigatorChildren";
import NavigatorPin from "./NavigatorPin";
import NavigatorObjectTitle from "./NavigatorObjectTitle";
import NavigatorHeaderMenu from "./NavigatorHeaderMenu";
import { scheduleC1NavigatorReveal, zoomC1NavigatorObject } from "./c1NavigatorZoom";
const mountedNavigators = new Set<NavigatorModel>();
const logger = new Logger();

let selectTimeout: any = 0;

/** Keeps the navigator header mounted across parent renders. */
function NavigationTreeHeader({
  headerTitle,
  headerMenu,
  appState,
  showSearch,
  root,
  excluded,
  pinned,
  onPin,
  onClose,
}: {
  headerTitle?: React.ReactNode;
  headerMenu?: "scenes" | "cabinets";
  appState: State;
  showSearch: boolean;
  root: BaseModel;
  excluded?: BaseModel | readonly BaseModel[];
  pinned?: BaseModel;
  onPin: (object: BaseModel | undefined) => void;
  onClose?: () => void;
}) {
  useOnChangeWatcher(appState.projectMetadata);
  useOnChangeWatcher(pinned);

  return (
    <div className="navigationTreeHeader">
      <NavigatorPin root={root} excluded={excluded} pinned={pinned} onPin={onPin} />
      {pinned ? (
        <div className="navigationTreeHeaderTitle navigationTreePinnedTitle">
          <div className="navigationTreePinnedTitleContent">
            <NavigatorObjectTitle object={pinned} />
          </div>
        </div>
      ) : headerTitle ? (
        <div className="navigationTreeHeaderTitle">{headerTitle}</div>
      ) : (
        <EditableText
          obj={appState.projectMetadata}
          property="displayName"
          propertyType="text"
          multiline={false}
          maxCharacters={appState.maxCharLimit.text}
          style={{ width: "calc(100% - 16px)", overflow: "hidden" }}
          onBlur={() => {
            appState.projectMetadata.isDirty = true;
          }}
        />
      )}
      {headerMenu && <NavigatorHeaderMenu kind={headerMenu} />}
      {onClose && (
        <button type="button" className="navigatorClose" data-testid="navigation-close-secondary"
          title={appState.translate("navigator.closeSecondary", "Unteren Navigator schließen")}
          aria-label={appState.translate("navigator.closeSecondary", "Unteren Navigator schließen")}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => { event.stopPropagation(); onClose(); }}>
          <i className="las la-times" aria-hidden="true" />
        </button>
      )}
      {showSearch && (
        <div
          className="menu-item-wrapper"
          style={{ padding: "1px 5px", borderRadius: "4px", cursor: "pointer" }}
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={appState.translate("treeSearch.search", "Search")}
          data-tooltip-place="bottom"
          onClick={() => {
            appState.treeSearchState.value = !appState.treeSearchState.value;
          }}
        >
          <i className="las la-search la-flip-horizontal" />
        </div>
      )}
    </div>
  );
}

export default function NavigationTree2(props: {
  onOpenSecondaryNavigator?: (target: SecondaryNavigatorTarget) => void;
  onClose?: () => void;
  rootObject?: BaseModel;
  headerTitle?: React.ReactNode;
  headerMenu?: "scenes" | "cabinets";
  instanceId?: string;
  excludedRootObject?: BaseModel | readonly BaseModel[];
  pinRequest?: { object: BaseModel | undefined };
  style?: React.CSSProperties;
  childrenProvider?: NavigatorChildren;
  showTags?: boolean;
  useBreakLines?: boolean;
  hideHeader?: boolean;
  refreshKey?: number | string;
  displayPathProvider?: (objects: readonly BaseModel[]) => readonly BaseModel[];
  renderPostfix?: TreeContext["renderPostfix"];
  onSelectObject?: TreeContext["onSelectObject"];
}) {
  const suffix = props.instanceId ?? "";
  const focusItem = useRef<any>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const rowId = useCallback(
    (source: "navigator" | "commander", id: string) => treeViewNodeId(source, id, props.instanceId),
    [props.instanceId],
  );
  const innerTreeRef = useRef<HTMLDivElement>(null);
  const navigationTreeScrollerRef = useRef<HTMLDivElement>(null);
  const baseRootObject = props.rootObject ?? state.root;
  const [pin, setPin] = useState<{ root: BaseModel; object: BaseModel }>();
  useOnChangeWatcher(pin?.object);
  const pinnedObject = pin?.root === baseRootObject && !pin.object.isDeleted ? pin.object : undefined;
  const rootObject = pinnedObject ?? baseRootObject;
  const onPin = useCallback(
    (object: BaseModel | undefined) => {
      // Reaching this navigator's normal root restores its unpinned state.
      setPin(object && object !== baseRootObject ? { root: baseRootObject, object } : undefined);
    },
    [baseRootObject],
  );
  // Apply each external request once; subsequent manual pin changes remain possible.
  useLayoutEffect(() => {
    if (props.pinRequest) onPin(props.pinRequest.object);
  }, [props.pinRequest, onPin]);
  useEffect(() => {
    if (pin && !pinnedObject) setPin(undefined);
  }, [pin, pinnedObject]);
  const excludedRoot = useRef(props.excludedRootObject);
  excludedRoot.current = props.excludedRootObject;
  const readChildren = useCallback(
    (node: Parameters<typeof navigatorChildren>[0]) => {
      const children = (props.childrenProvider ?? navigatorChildren)(node);
      return node.obj === baseRootObject && excludedRoot.current
        ? children.filter((child) =>
            Array.isArray(excludedRoot.current)
              ? !excludedRoot.current.includes(child.obj)
              : child.obj !== excludedRoot.current,
          )
        : children;
    },
    [baseRootObject, props.childrenProvider],
  );
  const navigatorModel = useMemo(
    () =>
      new NavigatorModel(
        rootObject,
        readChildren,
        props.displayPathProvider?.(state.selectionManager.displaySelections.getNavigatorPath()) ?? state.selectionManager.displaySelections.getNavigatorPath(),
        (object) => state.selectionManager.displaySelections.collapseNavigatorPath(object),
        () => props.useBreakLines ?? state.settings.useNavigatorBreakLines !== false,
        props.displayPathProvider,
      ),
    [rootObject, readChildren, props.displayPathProvider, props.useBreakLines],
  );
  useLayoutEffect(() => navigatorModel.refresh(), [navigatorModel, props.excludedRootObject, props.refreshKey, props.showTags]);
  const revealObject = useCallback(
    async (objectId: string) => {
      const object = state.objectRegistry.getObject(objectId);
      if (object instanceof BaseModel) navigatorModel.reveal(object);
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    },
    [navigatorModel],
  );
  useNavigatorConnectionEndpoints(state.connectionAnalyser, treeRef, rowId, undefined, revealObject);

  // Synchronisiert den Navigator vor dem Zeichnen des Browsers mit den sichtbaren Pfaden.
  // Bei einem Wechsel des navigatorModel wird der alte Effekt aufgeräumt und neu eingerichtet.
  useLayoutEffect(() => {
    const displaySelections = state.selectionManager.displaySelections;
    console.log("[NavigatorDebug] layoutEffect: connect", { root: navigatorDebugObject(navigatorModel.root.object), instance: props.instanceId ?? "main" });
    // Liefert die anzuzeigenden Objekte samt Eltern. setDisplayObjects berechnet daraus
    // die sichtbaren Baumknoten und öffnet die benötigten Eltern teilweise (partial).
    const refreshDisplay = () => {
      const path = displaySelections.getNavigatorPath();
      console.log("[NavigatorDebug] layoutEffect: refreshDisplay", {
        root: navigatorDebugObject(navigatorModel.root.object), selected: navigatorDebugObject(state.selectionManager.selectedObject),
        path: path.map(object => ({ object: navigatorDebugObject(object), parent: navigatorDebugObject(object.parent) })),
      });
      navigatorModel.setDisplayObjects(props.displayPathProvider?.(path) ?? path);
    };

    // Änderungen an den Baumobjekten beobachten und diesen Navigator für reveal registrieren.
    navigatorModel.connect();
    mountedNavigators.add(navigatorModel);

    // Den aktuellen Pfad sofort übernehmen und spätere Änderungen ebenfalls nachführen.
    refreshDisplay();
    const displaySubscription = displaySelections.onChange.subscribe(refreshDisplay);

    //
    // Darstellungsoptionen ändern die Baumansicht; dafür den bestehenden Pfad neu auswerten.
    //
    const refresh = () => navigatorModel.refresh();
    const uiSubscription = state.uiMode.onChange.subscribe(refresh);
    const flattenSubscription = state.flattenGroups.onChange.subscribe(refresh);
    const settingsSubscription = state.settings.onChange.subscribe(refresh);
    const modifiedSubscription = state._showModifiedStatus.onChange.subscribe(refresh);

    // Beim Entfernen der Komponente oder Modellwechsel alle Abonnements lösen,
    // einschließlich der Objekt-Abonnements innerhalb des Navigator-Modells.
    return () => {
      console.log("[NavigatorDebug] layoutEffect: cleanup", { root: navigatorDebugObject(navigatorModel.root.object) });
      mountedNavigators.delete(navigatorModel);
      displaySelections.onChange.unsubscribe(displaySubscription);
      state.uiMode.onChange.unsubscribe(uiSubscription);
      state.flattenGroups.onChange.unsubscribe(flattenSubscription);
      state.settings.onChange.unsubscribe(settingsSubscription);
      state._showModifiedStatus.onChange.unsubscribe(modifiedSubscription);
      navigatorModel.dispose();
    };
  }, [navigatorModel]);

  let flyoutMenus = true;

  if (Browser.getBrowser().indexOf("Firefox") >= 0) {
    flyoutMenus = false;
  }

  const startX = useRef<number>(0);

  const treeContext: TreeContext = {
    showTags: props.showTags,
    childrenProvider: props.childrenProvider,
    renderPostfix: props.renderPostfix,
    onSelectObject: props.onSelectObject,
    navigatorInstance: props.instanceId,
    onOpenSecondaryNavigator: props.onOpenSecondaryNavigator,
    navigatorModel,
    treename: "navigator",
    disableSingleClick: !state.settings.openWithOneClick,
    isGlobalSelectionTree: false,
    enableSelection: false,
  };

  useEffect(() => {
    const handle = panelResized.subscribe(adjustMenuButtons);

    if (innerTreeRef.current) {
      innerTreeRef.current?.addEventListener("scroll", adjustMenuButtons);
    }

    return () => {
      innerTreeRef?.current?.removeEventListener("scroll", adjustMenuButtons);
      panelResized.unsubscribe(handle);
    };
  }, []);

  useOnChangeWatcher(
    state.selectionManager,
    (changedProperty: string | any | undefined, changedObject: BaseObject, source?: string) => {
      if (focusItem.current) {
        focusItem.current = null;
      }

      let selectedObjectElm: any = null;

      let selectedObject = state.selectionManager.selectedObject;

      if (changedProperty == "highlighted") {
        const highLigthedObjects = state.selectionManager._highLigthedObjects;
        selectedObject = highLigthedObjects[highLigthedObjects.length - 1];
      }

      if (selectedObject) {
        selectedObjectElm = getTreeviewHtmlElement(treeContext, selectedObject as BaseModel);
      }

      console.log("[NavigatorDebug] selection watcher", {
        root: navigatorDebugObject(navigatorModel.root.object), changedProperty, object: navigatorDebugObject(selectedObject),
        rowFound: !!selectedObjectElm,
        action: selectedObjectElm ? "already visible" : !selectedObject ? "no selection"
          : selectedObject.isConsumer && changedProperty !== "highlighted" ? "setDisplayObjects" : "reveal",
      });

      if (selectedObjectElm) {
        // just select it, dont focus it anymore !!!!
        // selectedObjectElm.focus()
      } else {
        // looks like we need to expand our parent
        if (selectedObject?.isConsumer && changedProperty !== "highlighted") {
          const path = state.selectionManager.displaySelections.getNavigatorPath();
          navigatorModel.setDisplayObjects(props.displayPathProvider?.(path) ?? path);
        } else if (selectedObject) {
          navigatorModel.reveal(selectedObject);
        }
      }

      return false;
    },
  );

  /////////////////////////////////////////////////////////////////////////////////

  function onTouchStartResizeFiller(event: any) {
    startX.current = event.touches[0].screenX;
    event.stopPropagation();
  }

  function onTouchMoveResizeFiller(event: any) {
    const panel = treeRef.current?.closest<HTMLElement>("#navigationSidebar") ?? treeRef.current;
    if (!panel) return;
    const x = event.touches[0].screenX;
    const width = panel.getBoundingClientRect().width + x - startX.current;
    panel.style.width = `${Math.max(0, Math.min(window.innerWidth - 6, width))}px`;
    startX.current = x;
    panelResized.raise(undefined, undefined);
  }

  /////////////////////////////////////////////////////////////////////////////////
  // render

  const mobile = Browser.isMobile();

  function adjustMenuButtons() {
    if (!flyoutMenus) {
      return;
    }

    try {
      const navigationTreeScroller = innerTreeRef?.current; // document.getElementById("navigationTreeScroller");
      if (navigationTreeScroller) {
        const menus = navigationTreeScroller.querySelectorAll(".treeMenuButtonsWrapper");
        menus.forEach((menu) => {
          adjustMenu(menu as HTMLElement);
        });
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  return (
    <div
      ref={treeRef}
      id={`navigationTreeOuter${suffix}`}
      data-navigator-instance={suffix}
      className="navigationTreeOuter"
      tabIndex={1}
      style={{
        width: "300px",
        backgroundColor: "var(--panel-background)",
        boxShadow: "var(--panel-shadow)",
        borderRadius: "var(--panel-border-radius)",
        marginLeft: "5px",
        ...props.style,
      }}
    >
      {!props.hideHeader && <NavigationTreeHeader
        headerTitle={props.headerTitle}
        headerMenu={props.headerMenu}
        appState={state}
        showSearch={!props.instanceId}
        root={baseRootObject}
        excluded={props.excludedRootObject}
        pinned={pinnedObject}
        onPin={onPin}
        onClose={props.onClose}
      />}
      {!props.hideHeader && !props.instanceId && <SearchBar />}
      <div
        id={`navigationTree${suffix}`}
        onKeyDown={(ev) => {
          navigator_onKeyDown(ev, "navigator");
        }}
        style={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          overflowX: "hidden",
          zIndex: "1",
          display: "flex",
          flexDirection: "row",
          outline: "none",
          flexGrow: "1",
          flexShrink: "1",
          touchAction: "none",
        }}
      >
        <div
          id={`navigationTreeScroller${suffix}`}
          className="navigationTreeScroller"
          ref={navigationTreeScrollerRef}
          style={{
            position: "relative",
            width: "inherit",
            overflowY: "auto",
            overflowX: flyoutMenus ? "visible" : "hidden",

            display: "flex",
            flexDirection: "row",
          }}
          onScroll={(ev: any) => {
            adjustMenuButtons();
          }}
        >
          <div
            ref={innerTreeRef}
            style={{
              position: "relative",
              // width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: "1",
            }}
          >
            <NavigationTreeNodes
              navigatorNode={navigatorModel.root}
              key={rootObject.id}
              parent={rootObject}
              nesting={0}
              parentNodeInfo={navigatorModel.root.info}
              treeContext={treeContext}
              flyoutMenus={flyoutMenus}
            />

            <div
              className="ResizeFiller"
              onTouchStart={(ev) => onTouchStartResizeFiller(ev)}
              onTouchMove={(ev) => onTouchMoveResizeFiller(ev)}
            ></div>

            {!props.instanceId && <ContextMenu />}
          </div>
        </div>
      </div>

      {/* <PanelResizer bottomPanelId="ObjectPropertyView" minSize={45} />

      <ObjectPropertyView /> */}
    </div>
  );
}

//////////////////////////////////////////////////////////////////////////////////

export function treeViewNodeId(source: "navigator" | "commander", id: string, instanceId?: string): string {
  if (source == "commander") {
    return `gtv-${id}`;
  } else {
    return `tv${instanceId ?? ""}-${id}`;
  }
}

export function getTreeviewHtmlElement(
  treeContext: TreeContext | "navigator" | "commander",
  obj: BaseModel | undefined,
): HTMLElement | null {
  if (!obj) {
    return null;
  }

  let treename: "navigator" | "commander" = "navigator";
  if (typeof treeContext == "object") {
    treename = treeContext.treename;
  } else {
    treename = treeContext;
  }

  const instanceId = typeof treeContext === "object" ? treeContext.navigatorInstance : undefined;
  if (treename === "commander") return document.getElementById(treeViewNodeId(treename, obj.id));
  const trees =
    typeof treeContext === "object"
      ? document.querySelectorAll(`[data-navigator-instance="${CSS.escape(instanceId ?? "")}"]`)
      : document.querySelectorAll("[data-navigator-instance]");
  for (const tree of Array.from(trees)) {
    const id = treeViewNodeId("navigator", obj.id, tree.getAttribute("data-navigator-instance") ?? "");
    for (const row of Array.from(tree.querySelectorAll<HTMLElement>(`[id="${CSS.escape(id)}"]`))) {
      // Children shown below a reference share IDs with their original rows.
      // They must not prevent revealing the object's actual ancestor path.
      const reference = row.closest("[data-navigator-reference]");
      if (
        !reference ||
        (reference === row.parentElement && reference.getAttribute("data-navigator-reference") === obj.id)
      )
        return row;
    }
  }
  return null;
}

export function isChildOfMe(me: BaseModel, childObj: BaseModel | undefined) {
  let parent = childObj?.parent;
  while (parent) {
    if (me == parent) {
      return true;
    } else {
      parent = parent.parent;
    }
  }
  return false;
}

//
// return  0 for visible inSide Viewport
//        -1 outside, above
//         1 outside below
//

export function elementIsVisibleInViewport(elm: HTMLElement): number {
  const viewport = elm?.closest(".navigationTreeScroller") ?? document.getElementById("navigationTreeScroller");

  if (elm && viewport) {
    let { top, bottom } = elm.getBoundingClientRect();
    const vpRect = viewport.getBoundingClientRect();

    top -= vpRect.top;
    bottom -= vpRect.top;

    const s = viewport.scrollTop;

    if (top < 0) return -1;

    if (bottom > vpRect.height) return 1;
  }

  return 0;
}

//////////////////////////////////////////////////////////////////
function selectElement(obj1: BaseObject) {
  if (obj1) {
    if (selectTimeout) {
      clearTimeout(selectTimeout);
    }

    state.selectionManager.selectedObject = undefined;

    selectTimeout = setTimeout(() => {
      selectTimeout = 0;
      state.selectionManager.setSelectedObjectWithoutScrolling(obj1 as BaseModel);
    }, 200);
  }
}

function focusElm(elm: any | undefined) {
  if (elm) {
    let id: string = (elm as any).id;
    // strip "tv-" prefix from id
    const idx = id.indexOf("-");
    const idPrefix = id.substring(0, idx);
    id = id.substring(idx + 1);

    const obj1 = navigatorRows.get(elm)?.object ?? state.objectRegistry.getObject(id);
    if (elm.matches?.(".C1_navigator .dataline")) {
      // C1 row focus selects immediately and defers the complete dock transition by 500 ms.
      // The legacy undefined-selection step would close the previous editor before that timer.
      if (selectTimeout) { clearTimeout(selectTimeout); selectTimeout = 0; }
      elm.focus();
      if (obj1?.treeViewModel.isSelectable) scheduleC1NavigatorReveal(obj1 as BaseModel, elm);
      return;
    }
    // Category rows retain the current object selection and its expanded display path.
    if (obj1?.treeViewModel.isSelectable) {
      if (navigatorRows.has(elm) || idPrefix == "tv") {
        selectElement(obj1);
      } else {
        state.selectionManager.setSelectedObjectWithoutNavigatorUpdate(obj1 as BaseModel);
      }
    }

    state.selectionManager.focusedObjectInNavigator?.treeViewModel?.handleClose(true);

    elm?.focus();
  }
}

//////////////////////////////////////////////////////////////////////////////////
export function navigator_onKeyDown(ev: any, source: string) {
  // Leave editor shortcuts (including Delete) to the surrounding application.
  if (![13, 32, 37, 38, 39, 40].includes(ev.keyCode)) return;
  // Space activates C1 object rows; text fields and menu buttons keep their native behavior.
  if (ev.keyCode === 32 && !document.activeElement?.matches(".C1_navigator .dataline")) return;

  if (document.activeElement) {
    if (document.activeElement.hasAttribute("contentEditable")) {
      return;
    }

    if (document.activeElement.tagName == "INPUT") {
      return;
    }
  }

  ev.preventDefault();

  const focusedElement = document.activeElement as HTMLElement;

  let id = focusedElement?.id;

  if (id) {
    // strip "tv-" prefix from id
    const idx = id.indexOf("-");
    id = id.substring(idx + 1);

    state.selectionManager.focusedObjectInNavigator = navigatorRows.get(focusedElement)?.object ?? state.objectRegistry.getObject(id) as BaseModel;
  }

  if (focusedElement && state.selectionManager.focusedObjectInNavigator) {
    // Break buttons are not object rows; arrow navigation skips them.
    if ((ev.keyCode === 38 || ev.keyCode === 40) && navigatorRows.has(focusedElement)) {
      const tree = focusedElement.closest(".navigationTreeOuter");
      const rows = Array.from(tree?.querySelectorAll<HTMLElement>(".dataline") ?? []).filter((row) =>
        navigatorRows.has(row),
      );
      const index = rows.indexOf(focusedElement);
      if (index >= 0) focusElm(rows[index + (ev.keyCode === 40 ? 1 : -1)]);
      return;
    }
    switch (ev.keyCode) {
      case 13:
      case 32:
        if (!zoomC1NavigatorObject(state.selectionManager.focusedObjectInNavigator)) {
          state.selectionManager.focusedObjectInNavigator.treeViewModel.handleOpen(true);
        }
        break;

      case 40: //down
        if (
          focusedElement?.classList.contains("expanded") &&
          state.selectionManager.focusedObjectInNavigator.treeViewModel.hasChildren
        ) {
          focusElm(focusedElement?.nextSibling?.firstChild?.firstChild);
        } else {
          const nextSibling = focusedElement.parentNode?.nextSibling;
          if (nextSibling == undefined) {
            // no more nodes on this level.
            // we need to go one or more levels up
            let p1 = focusedElement.parentNode?.parentNode?.parentNode;
            let again = false;
            do {
              again = false;
              if (p1?.nextSibling) {
                focusElm(p1?.nextSibling?.firstChild);
              } else {
                p1 = p1?.parentNode?.parentNode;
                if (p1) {
                  again = true;
                }
              }
            } while (again);
          } else {
            focusElm(focusedElement.parentNode?.nextSibling?.firstChild);
          }
        }
        break;

      case 38: //up
        const previousSibling = focusedElement.parentNode?.previousSibling;
        if (previousSibling == undefined) {
          // no more nodes on this level.
          // we need to go one or more levels up
          const p1 = focusedElement.parentNode?.parentNode?.parentNode;
          focusElm(p1?.firstChild);
        } else {
          let elm1 = focusedElement.parentNode?.previousSibling?.firstChild as any;
          elm1 = lastNestedChildNode(elm1);
          focusElm(elm1);
        }

        break;

      case 37: //LEFT
        if (focusedElement.classList.contains("expanded")) {
          collapseItem(focusedElement, state.selectionManager.focusedObjectInNavigator, source);
        } else {
          // navigate to parent
          const p1 = focusedElement.parentNode?.parentNode?.parentNode;
          focusElm(p1?.firstChild);
        }

        break;

      case 39: //right
        expandItem(focusedElement, state.selectionManager.focusedObjectInNavigator, source);
        break;

      default:
      // logger.log("unused key: ", ev.keyCode);
    }
  }
}

function lastNestedChildNode(elm: any): any {
  if (elm) {
    if (elm.classList.contains("expanded")) {
      const elm1 = elm.parentNode?.lastChild?.lastChild?.firstChild;

      if (elm1.id.indexOf("tv-") < 0) {
        // to check if elm1 is a treeNode
        // if not if is the menu or something !!!!
        return elm;
      }

      if (elm1 == undefined) {
        return elm;
      }
      const elm2 = lastNestedChildNode(elm1);
      if (elm2 == undefined) {
        return elm1;
      } else {
        return elm2;
      }
    }
  }
  return elm;
}

/** Opens the same ancestor hierarchy as selection, without selecting or focusing the target. */
export async function revealNavigatorObject(objectId: string): Promise<void> {
  const object = state.objectRegistry.getObject<BaseModel>(objectId);
  if (!object || object.isDeleted) return;
  for (const navigator of mountedNavigators) navigator.reveal(object);
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

function expandItem(elm: HTMLElement | undefined, obj: BaseObject, source: string) {
  logger.log(`mwe1 expandParentHirachie raiseOnChange ${obj?.className}`);

  if (elm) {
    const node = navigatorRows.get(elm);
    if (node) {
      node.setExpanded(true);
      return;
    }
    const expandEvent = new CustomEvent<any>("expand", {
      detail: { action: "expand" },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    elm.dispatchEvent(expandEvent);
  }

  // obj.raiseOnChange("expanded", source, true);
}

function collapseItem(elm: HTMLElement | undefined, obj: BaseObject, source: string) {
  if (elm) {
    const node = navigatorRows.get(elm);
    if (node) {
      node.setExpanded(false);
      return;
    }
    const expandEvent = new CustomEvent<any>("expand", {
      detail: { action: "collapse" },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    elm.dispatchEvent(expandEvent);
  }

  // obj.raiseOnChange("collapsed", source);
}
